/* eslint-disable no-bitwise */
import { hierarchy as d3Hierarchy, partition as d3Partition } from 'd3-hierarchy';
import { select as d3Select } from 'd3-selection';
import {
    HORIZONTAL_SPACING,
    MIN_DISPLAY_SIZE,
    TEXT_COLOR,
    TEXT_FONT,
    TEXT_POSITION_IN_RECTANGLE_X,
    VERTICAL_SPACING,
} from './consts';
import { D3DataTreeNode, NodeDataSearch, RectangularNode } from './types';

interface Dimensions {
    height: number;
    width: number;
}

export const partition = (data: D3DataTreeNode, { height, width }: Dimensions): RectangularNode => {
    const root = d3Hierarchy<D3DataTreeNode>(data).sum(({ children, value }) =>
        children ? 0 : value,
    );

    return d3Partition<D3DataTreeNode>().size([width, ((root.height + 1) * height) / root.height])(
        root,
    ) as RectangularNode;
};

export const getMaxDepth = (array: D3DataTreeNode[]): number => {
    if (!array.length) {
        return 0;
    }

    return 1 + Math.max(...array.map(({ children = [] }) => getMaxDepth(children)));
};

export const getRectWidth = (x0: number, x1: number): number => {
    const fullWidth = x1 - x0;

    return fullWidth - Math.min(HORIZONTAL_SPACING, fullWidth / 2);
};

export const getRectWidthFromNode = ({ x0, x1 }: RectangularNode): number => getRectWidth(x0, x1);

export const getRectHeight = ({ y0, y1 }: RectangularNode): number => y1 - y0 - VERTICAL_SPACING;

export const getTitle = ({ data: { title } }: RectangularNode): string => title;

export const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    fullWidth: number,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    radius = 0,
    alpha = 1,
): void => {
    if (width < MIN_DISPLAY_SIZE || height < MIN_DISPLAY_SIZE || x + width < 0 || x > fullWidth) {
        return;
    }
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    const r = Math.min(radius, width / 2, height / 2);
    if (r > 1) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + width, y, x + width, y + height, r);
        ctx.arcTo(x + width, y + height, x, y + height, r);
        ctx.arcTo(x, y + height, x, y, r);
        ctx.arcTo(x, y, x + width, y, r);
        ctx.closePath();
        ctx.fill();
    } else {
        ctx.fillRect(x, y, width, height);
    }
    ctx.globalAlpha = 1;
};

export const drawText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x = 0,
    y = 0,
    fontDecorator = '',
): void => {
    if (fontDecorator) {
        ctx.font = `${fontDecorator} ${TEXT_FONT}`;
    } else {
        ctx.font = TEXT_FONT;
    }
    ctx.fillStyle = TEXT_COLOR;
    ctx.fillText(text, x, y);
};

/*
    Maps an index to a color from a sequence, going from 0,0,0 to 255,255,255.
*/
export const getColor = (i: number): string => {
    const ret = [];
    // via http://stackoverflow.com/a/15804183
    if (i < 16777215) {
        ret.push(i & 0xff); // R
        ret.push((i & 0xff00) >> 8); // G
        ret.push((i & 0xff0000) >> 16); // B
    }
    const col = `rgb(${ret.join(',')})`;

    return col;
};

export const truncateTitle = (
    ctx: CanvasRenderingContext2D,
    title: string,
    width: number,
): string => {
    const textWidth = ctx.measureText(title).width;
    const charWidth = textWidth / title.length;
    const textMaxWidth = width - 2 * TEXT_POSITION_IN_RECTANGLE_X;
    const truncatedSuffix = '…';

    if (textWidth < textMaxWidth) {
        return title;
    }
    // Slice string from 0 to the max number of characters minus 2 (2 is approx. '...' length)
    const truncated = title.substr(0, Math.max(0, Math.floor(textMaxWidth / charWidth)) - 2);

    return truncated ? `${truncated}${truncatedSuffix}` : '';
};

const getScrollBarWidth = (): number => {
    const inner = document.createElement('p');
    inner.style.width = '100%';
    inner.style.height = '200px';

    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '200px';
    outer.style.height = '150px';
    outer.style.overflow = 'hidden';
    outer.appendChild(inner);

    document.body.appendChild(outer);
    const w1 = inner.offsetWidth;
    outer.style.overflow = 'scroll';
    let w2 = inner.offsetWidth;
    if (w1 === w2) w2 = outer.clientWidth;

    document.body.removeChild(outer);

    return w1 - w2;
};

export const getInitialWidth = (container: HTMLElement): number => {
    // Takes scrollbar  width into account (if it does not exists on the page yet)
    const { width: initialWidth } = container.getBoundingClientRect();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (document?.getElementById('app')?.clientWidth < window.innerWidth) {
        return initialWidth;
    }

    return initialWidth - getScrollBarWidth();
};

export const getCanvasContext = (
    id: string,
    containerId: string,
    width: number,
    height: number,
    devicePixelRatio: number,
    isHidden = false,
    willReadFrequently = false,
): CanvasRenderingContext2D => {
    const canvas = d3Select(`#${containerId}`)
        .append('canvas')
        .attr('id', id)
        .attr('width', width * devicePixelRatio)
        .attr('height', height * devicePixelRatio);

    if (isHidden) {
        canvas.style('display', 'none');
    }
    const context = canvas
        ?.node()
        ?.getContext('2d', { willReadFrequently }) as CanvasRenderingContext2D;
    context?.scale(devicePixelRatio, devicePixelRatio);

    return context;
};

/**
 * Returns closest node matching given path
 *
 * Ex: Input path "consommation/1/2/3"
 *
 * => Searches node with path "consommation/1/2/3"
 *
 * => If not found, searches node with path "consommation/1/2"
 *
 * => ...
 *
 * => Returns parent node if no match is found
 *
 * @param nodesArray Array of every node of the tree
 * @param nodePath String representing node id path
 * @returns Closest matching node
 */
export const getNodeFromPath = (
    nodesArray: RectangularNode[],
    nodePath: string,
): RectangularNode => {
    let currentNodePath = nodePath;
    while (currentNodePath) {
        // eslint-disable-next-line no-loop-func
        const desiredNode = nodesArray.find((node) => node.data.path === currentNodePath);
        if (desiredNode) {
            return desiredNode;
        }
        currentNodePath = currentNodePath.split('/').slice(0, -1).join('/');
    }

    return nodesArray[0];
};

export const getBaseUrl = (pathname: string): string => {
    if (pathname.endsWith('[path]')) {
        return pathname.substring(0, pathname.length - '/[path]'.length);
    }

    return pathname;
};

export const addThemeToPath = (nodes: D3DataTreeNode, path?: string): string => {
    const themes = nodes.children;

    if (!path || !themes || !themes.length) {
        return '';
    }

    const nodeIds = path.split('/');
    const alreadyHasTheme = themes.some((theme) => theme.id === nodeIds[0]);

    if (alreadyHasTheme) {
        return path;
    }

    const nodeTheme: string = themes.reduce<string>(
        (theme: string, { children, id }) =>
            theme || (children?.some((child) => child.id === nodeIds[0]) ? id : ''),
        '',
    );

    return nodeTheme ? `${nodeTheme}/${path}` : path;
};

// TODO pre-compute path and store in tree
export const getNodeTitlePath = (node: RectangularNode): string[] =>
    node
        .ancestors()
        .map(({ data: { title } }) => title)
        .reverse();

/**
 * Filter by keywordHierarchy facet to get every document from a node
 * and its descendant.
 *
 * Ex :
 * - Path : ['Title 1', 'Chapter A', 'Page 3'])
 * - Facet name : keywordHierarchy.lvl2
 * - Value : Title 1 > Chapter A > Page 3
 */
export const getKeywordFilter = (path: string[]): string => {
    const facet = `keywordHierarchy.lvl${path.length - 1}`;
    const value = path.join(' > ');

    return `${facet}:"${value}"`;
};

export const shortenCountryInTitle = (title: string): string => {
    return title
        .replace(/(fr)(ançais|ench)/gi, '$1.')
        .replace(/european/gi, 'EU')
        .replace(/européen/gi, 'eu.');
};

/**
 * Search in tree -- at least one word must match in a node title and
 * every word must match at least once in the node's path
 * @param searchTree -- simplified tree for search
 * @param searchQuery -- normalized and splitted by word search query
 * @returns -- filtered original tree used to draw highlighted found nodes
 */
export const treeSearch = (
    searchTree: NodeDataSearch[],
    searchQuery: string[],
): NodeDataSearch[] => {
    const matchedNodes = searchTree.filter(({ titlePath, title }) => {
        // At least one word must match in a node title, 'some' checks that condition
        if (searchQuery.some((word) => title.includes(word))) {
            // if previous condition is met, check every word matches at least once in the node's path
            if (
                searchQuery.every((word) =>
                    titlePath.some((parentTitle) => parentTitle.includes(word)),
                )
            ) {
                return true;
            }
        }

        return false;
    });

    return matchedNodes;
};
